import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  requestPortalOtp,
  verifyPortalOtp,
  loadCandidatePortal,
  patchPortalProgress,
  submitPortalFormAnswers,
  finalSubmitPortal,
  uploadPortalFormFile,
} from '../../../Redux/ActionCreators/onboardingAction';
import { fetchTenantBranding } from '../../../Redux/ActionCreators/qualifyQuestionsAction';
import './CandidatePortal.scss';

const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
};

const getFieldInputType = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('long') || t === 'textarea') return 'textarea';
  if (t.includes('select') || t.includes('dropdown')) return 'select';
  if (t.includes('checkbox')) return 'checkbox';
  if (t.includes('file')) return 'file';
  if (t.includes('number')) return 'number';
  if (t.includes('date')) return 'date';
  return 'text';
};

// ─── Snackbar ─────────────────────────────────────────────────────────────────
const Snackbar = ({ message, onClose }) => {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [message, onClose]);
  if (!message) return null;
  return (
    <div className="cp-snackbar">
      <span className="cp-snackbar__msg">{message}</span>
      <button className="cp-snackbar__close" onClick={onClose}>✕</button>
    </div>
  );
};

// ─── Step Pills ───────────────────────────────────────────────────────────────
const StepPills = ({ steps, currentStep }) => (
  <div className="cp-step-pills">
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <div className={`cp-pill ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'done' : ''}`}>
          <span className="cp-pill__num">{i < currentStep ? '✓' : i + 1}</span>
          <span className="cp-pill__label">{s}</span>
        </div>
        {i < steps.length - 1 && <div className={`cp-pill-connector ${i < currentStep ? 'done' : ''}`} />}
      </React.Fragment>
    ))}
  </div>
);

// ─── Progress bar ─────────────────────────────────────────────────────────────
const PortalProgress = ({ current, total, forceComplete }) => {
  const pct = forceComplete ? 100 : (total > 0 ? Math.round((current / total) * 100) : 0);
  return (
    <div className="cp-progress">
      <div className="cp-progress__bar">
        <div className="cp-progress__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="cp-progress__label">{pct}% complete</span>
    </div>
  );
};

// ─── OTP Gate ─────────────────────────────────────────────────────────────────
const OtpGate = ({ maskedEmail, otpInput, setOtpInput, onSubmit, onResend, error, resendMsg, saving, resendCooldown }) => (
  <div className="cp-otp-gate">
    <div className="cp-otp-gate__icon">🔐</div>
    <h2 className="cp-otp-gate__title">Verify Your Identity</h2>
    <p className="cp-otp-gate__sub">
      We sent a 6-digit code to <strong>{maskedEmail}</strong>. Enter it below to access your onboarding portal.
    </p>
    {error && <div className="cp-otp-gate__error">{error}</div>}
    {resendMsg && <div className="cp-otp-gate__resend-msg">{resendMsg}</div>}
    <input
      className="cp-field__input cp-otp-gate__input"
      type="text"
      inputMode="numeric"
      maxLength={6}
      placeholder="Enter 6-digit OTP"
      value={otpInput}
      onChange={e => setOtpInput(e.target.value.replace(/\D/g, ''))}
    />
    <div className='d-flex flex-wrap gap-2 mt-2'>
      <button
        className="cp-btn cp-otp-gate__resend"
        onClick={onResend}
        disabled={resendCooldown > 0}
      >
        {resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : 'Resend OTP'}
      </button>
      <button
        className="cp-btn cp-btn--primary cp-otp-gate__btn"
        onClick={onSubmit}
        disabled={saving || otpInput.length < 6}
      >
        {saving ? 'Verifying…' : 'Verify & Continue'}
      </button>
    </div>
  </div>
);

// ─── Welcome Step ─────────────────────────────────────────────────────────────
const WelcomeSection = ({ run, welcome = {}, forms, documents, onNext }) => {
  const embedUrl = welcome.video_url ? getEmbedUrl(welcome.video_url) : null;
  const stepItems = [
    run?.step_flags?.forms && forms.length > 0 && { icon: '📝', label: 'Fill out required forms' },
    run?.step_flags?.documents && documents.length > 0 && { icon: '📄', label: 'Review your documents' },
  ].filter(Boolean);

  return (
    <div className="cp-section">
      <div className="cp-welcome">
        <div className="cp-welcome__card">
          <div className="cp-welcome__avatar">🏢</div>
          <h2 className="cp-welcome__title">
            {welcome.title || `Welcome, ${run?.candidate_name || 'there'}!`}
          </h2>
          {welcome.message ? (
            <div
              className="cp-welcome__msg cp-welcome__msg--html"
              dangerouslySetInnerHTML={{ __html: welcome.message }}
            />
          ) : (
            <p className="cp-welcome__msg">
              We're thrilled to have you join the team. Please complete the steps below to finish your onboarding.
            </p>
          )}
          {embedUrl && (
            <div className="cp-welcome__video">
              <iframe
                src={embedUrl}
                title="Welcome Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>
        {stepItems.length > 0 && (
          <div className="cp-welcome__steps">
            {stepItems.map((s, i) => (
              <div key={i} className="cp-welcome__step-item">
                <span>{s.icon}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="cp-section__footer">
        <button className="cp-btn cp-btn--primary" onClick={onNext}>
          Get Started
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// ─── Single Form View ─────────────────────────────────────────────────────────
const FormDetail = ({ form, formIdx, totalForms, answers, setAnswers, onSave, onBack, saving, onFileUpload }) => {
  const formKey = form.id ?? form.name ?? formIdx;
  const [fieldErrors, setFieldErrors] = useState({});

  const setAnswer = (fieldKey, val) => {
    setFieldErrors(prev => ({ ...prev, [fieldKey]: '' }));
    setAnswers(prev => ({
      ...prev,
      [formKey]: { ...(prev[formKey] || {}), [fieldKey]: val },
    }));
  };

  const validateAndSave = (form, formKey) => {
    const currentAnswers = answers[formKey] || {};
    const errors = {};
    (form.fields || []).forEach((field, fieldIdx) => {
      if (!field.required) return;
      const fieldKey = field.id ?? `${field.label ?? ''}_${fieldIdx}`;
      const val = currentAnswers[fieldKey];
      const inputType = getFieldInputType(field.type);
      let isEmpty = false;
      if (inputType === 'checkbox') {
        isEmpty = !val;
      } else if (inputType === 'file') {
        isEmpty = !val;
      } else {
        isEmpty = val === undefined || val === null || String(val).trim() === '';
      }
      if (isEmpty) errors[fieldKey] = `${field.label || 'This field'} is required.`;
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) onSave(form, formKey);
  };

  return (
    <div className="cp-section">
      <div className="cp-form-detail-header">
        <button className="cp-btn cp-btn--sm" onClick={onBack}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Forms
        </button>
        <span className="cp-form-detail-counter">Form {formIdx + 1} of {totalForms}</span>
      </div>

      <h2 className="cp-section__title" style={{ marginTop: 16 }}>{form.name}</h2>
      {form.description && <p className="cp-section__sub">{form.description}</p>}

      <div className="cp-form-card__fields">
        {(form.fields || []).map((field, fieldIdx) => {
          const fieldKey = field.id ?? `${field.label ?? ''}_${fieldIdx}`;
          const inputType = getFieldInputType(field.type);
          const currentVal = (answers[formKey] || {})[fieldKey];
          return (
            <div key={fieldKey} className="cp-field">
              <label className="cp-field__label">
                {field.label}
                {field.required && <span className="cp-required">*</span>}
              </label>
              {inputType === 'textarea' ? (
                <textarea
                  className="cp-field__input"
                  rows={3}
                  required={field.required}
                  value={currentVal || ''}
                  onChange={e => setAnswer(fieldKey, e.target.value)}
                />
              ) : inputType === 'select' ? (
                <select
                  className="cp-field__input"
                  required={field.required}
                  value={currentVal || ''}
                  onChange={e => setAnswer(fieldKey, e.target.value)}
                >
                  <option value="">Select…</option>
                  {(Array.isArray(field.options) ? field.options : (field.options || '').split(',')).map(o => String(o).trim()).filter(Boolean).map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : inputType === 'checkbox' ? (
                <label className="cp-field__checkbox">
                  <input
                    type="checkbox"
                    required={field.required}
                    checked={!!currentVal}
                    onChange={e => setAnswer(fieldKey, e.target.checked)}
                  />
                  <span>{field.label}</span>
                </label>
              ) : inputType === 'file' ? (
                <div>
                  <input
                    type="file"
                    className="cp-field__input"
                    accept=".pdf,.docx,.csv,image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('file', file);
                      const result = await onFileUpload(form.id, fieldIdx, fd);
                      if (result) setAnswer(fieldKey, result);
                      e.target.value = '';
                    }}
                  />
                  <span className="cp-field__file-hint">Accepted formats: PDF, DOCX, CSV, or any image (JPG, PNG, etc.)</span>
                  {currentVal && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#22c55e', marginTop: 4 }}>
                      <span>✓ {typeof currentVal === 'object' && currentVal?.file_name ? currentVal.file_name : 'File uploaded'}</span>
                      {typeof currentVal === 'object' && (currentVal?.file_url || currentVal?.url) && (
                        <a
                          href={currentVal.file_url || currentVal.url}
                          download={currentVal.file_name || true}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Download file"
                          style={{ color: '#22c55e', lineHeight: 1, flexShrink: 0 }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <input
                  className="cp-field__input"
                  type={inputType}
                  required={field.required}
                  value={typeof currentVal === 'object' ? '' : (currentVal || '')}
                  onChange={e => setAnswer(fieldKey, e.target.value)}
                />
              )}
              {fieldErrors[fieldKey] && (
                <span className="cp-field__error">{fieldErrors[fieldKey]}</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="cp-section__footer">
        <button className="cp-btn" onClick={onBack}>Cancel</button>
        <button className="cp-btn cp-btn--primary" onClick={() => validateAndSave(form, formKey)} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

// ─── Forms Step ───────────────────────────────────────────────────────────────
const FormsSection = ({ forms, formAnswers, setFormAnswers, formsSubmitted, onFormSave, onNext, onBack, saving, onFileUpload }) => {
  const [activeFormIdx, setActiveFormIdx] = useState(-1);

  const submittedCount = Object.keys(formsSubmitted).length;
  const allSubmitted = submittedCount === forms.length;

  if (activeFormIdx >= 0) {
    const form = forms[activeFormIdx];
    return (
      <FormDetail
        form={form}
        formIdx={activeFormIdx}
        totalForms={forms.length}
        answers={formAnswers}
        setAnswers={setFormAnswers}
        onSave={async (f, formKey) => {
          await onFormSave(f, formKey);
          setActiveFormIdx(-1);
        }}
        onBack={() => setActiveFormIdx(-1)}
        saving={saving}
        onFileUpload={onFileUpload}
      />
    );
  }

  return (
    <div className="cp-section">
      <div className="cp-section-header">
        <h2 className="cp-section__title">Complete Your Forms</h2>
        <span className="cp-forms-counter">{submittedCount} of {forms.length} submitted</span>
      </div>
      <p className="cp-section__sub">Please fill out and submit all required forms before continuing.</p>

      <div className="cp-forms-list">
        {forms.map((form, idx) => {
          const formKey = form.id ?? form.name ?? idx;
          const isSubmitted = !!formsSubmitted[form.id ?? formKey];
          const hasAnswers = !!(formAnswers[formKey] && Object.keys(formAnswers[formKey]).length > 0);
          return (
            <div key={formKey} className={`cp-form-row ${isSubmitted ? 'submitted' : ''}`}>
              <div className="cp-form-row__number" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '50%', background: '#f3f4f6', fontWeight: 600, fontSize: 18,}}>
                {idx + 1}
                </div>
              <div className="cp-form-row__info">
                <div className="cp-form-row__name">{form.name}</div>
                <div className="cp-form-row__meta">{(form.fields || []).length} fields</div>
              </div>
              <div className="cp-form-row__status">
                {isSubmitted ? (
                  <div className="cp-status-badge cp-status-badge--done">✓ Submitted</div>
                ) : hasAnswers ? (
                  <div className="cp-status-badge cp-status-badge--progress">In Progress</div>
                ) : (
                  <div className="cp-status-badge cp-status-badge--pending">Pending</div>
                )}
              </div>
              <div className="cp-form-row__action">
                <button
                  className={`cp-btn ${isSubmitted ? 'cp-btn--sm' : 'cp-btn--primary cp-btn--sm'}`}
                  onClick={() => setActiveFormIdx(idx)}
                >
                  {isSubmitted ? 'Edit' : hasAnswers ? 'Continue' : 'Start'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="cp-section__footer">
        <button className="cp-btn" onClick={onBack}>Back</button>
        <button className="cp-btn cp-btn--primary" onClick={onNext} disabled={saving || !allSubmitted}>
          {saving ? 'Saving…' : 'Continue to Next Step'}
        </button>
      </div>
    </div>
  );
};

// ─── Document Viewer Modal ────────────────────────────────────────────────────
const DocViewerModal = ({ doc, isAccepted, onAccept, onClose }) => {
  const [hasOpenedLink, setHasOpenedLink] = React.useState(false);
  const fileUrl = doc.file_url || doc.url;
  const needsAccept = !!doc.requires_accept;
  const mimeType = doc.content_type || doc.mimeType || '';
  const ext = (doc.name || '').split('.').pop().toLowerCase();

  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext);
  const isPdf = mimeType.includes('pdf') || ext === 'pdf';
  const isDocx = mimeType.includes('word') || ext === 'docx' || ext === 'doc';
  const hasInlinePreview = isImage || isPdf || isDocx;

  // For files with no inline preview, require the user to open the link before accepting
  const needsOpenFirst = needsAccept && fileUrl && !hasInlinePreview && !hasOpenedLink;

  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={e => e.stopPropagation()}>
        <div className="cp-modal__header">
          <span className="cp-modal__title">{doc.name}</span>
          <button className="cp-modal__close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="cp-modal__body">
          {fileUrl ? (
            isImage ? (
              <img src={fileUrl} alt={doc.name} style={{maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto'}} />
            ) : isPdf ? (
              <iframe src={fileUrl} title={doc.name} className="cp-modal__iframe" />
            ) : isDocx ? (
              <iframe src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`} title={doc.name} className="cp-modal__iframe" />
            ) : (
              <div style={{textAlign: 'center'}}>
                <div className="cp-modal__no-preview">No preview available for this file type.</div>
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cp-btn"
                  style={{marginTop: 16, display: 'inline-block'}}
                  onClick={() => setHasOpenedLink(true)}
                >
                  Open File in New Tab
                </a>
              </div>
            )
          ) : (
            <div className="cp-modal__no-preview">No preview available for this file.</div>
          )}
        </div>
        <div className="cp-modal__footer">
          <button className="cp-btn" onClick={onClose}>Close</button>
          {needsAccept && (
            <>
              {needsOpenFirst && (
                <span className="cp-modal__accept-hint">Open the file first to enable acceptance.</span>
              )}
              <button
                className="cp-btn cp-btn--primary"
                onClick={() => { onAccept(doc.id); onClose(); }}
                disabled={isAccepted || needsOpenFirst}
              >
                {isAccepted ? '✓ Accepted' : 'Accept Document'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Documents Step ───────────────────────────────────────────────────────────
const DocumentsSection = ({ documents, onNext, onBack, accepted, setAccepted, viewed, setViewed }) => {
  const [viewingDoc, setViewingDoc] = React.useState(null);

  const fileIconConfig = (mimeType = '') => {
    if (mimeType.includes('pdf')) return { bg: '#fee2e2', color: '#991b1b', label: 'PDF' };
    if (mimeType.includes('word') || mimeType.includes('doc')) return { bg: '#dbeafe', color: '#1e40af', label: 'DOC' };
    return { bg: '#f3f4f6', color: '#6b7280', label: 'FILE' };
  };

  const acceptDocs = documents.filter(d => !!d.requires_accept);
  const readOnlyDocs = documents.filter(d => !d.requires_accept);
  const allAccepted = acceptDocs.every(d => accepted[d.id]);
  const allRead = readOnlyDocs.every(d => viewed[d.id]);
  const canContinue = allAccepted && allRead;

  const handleAccept = (docId) => {
    setAccepted(prev => ({ ...prev, [docId]: true }));
  };

  const handleView = (doc) => {
    setViewed(prev => ({ ...prev, [doc.id]: true }));
    setViewingDoc(doc);
  };

  return (
    <div className="cp-section">
      <h2 className="cp-section__title">Review Documents</h2>
      <p className="cp-section__sub">Please review the following documents before proceeding.</p>

      <div className="cp-docs">
        {documents.map(doc => {
          const icon = fileIconConfig(doc.content_type || doc.mimeType || '');
          const needsAccept = !!doc.requires_accept;
          const hasFile = !!(doc.file_url || doc.url);
          return (
            <div key={doc.id} className={`cp-doc-item ${accepted[doc.id] ? 'accepted' : ''}`}>
              <div className="cp-doc-item-row">
                <div className="cp-doc-item__icon" style={{ background: icon.bg, color: icon.color }}>
                  {icon.label}
                </div>
                <div className="cp-doc-item__info">
                  <div className="cp-doc-item__name">{doc.name}</div>
                  {doc.description && <div className="cp-doc-item__desc">{doc.description}</div>}
                  <div className="cp-doc-item__mode">
                    {needsAccept ? (
                      <span className="cp-doc-badge cp-doc-badge--accept">Requires acceptance</span>
                    ) : (
                      <span className="cp-doc-badge cp-doc-badge--read">Read only</span>
                    )}
                  </div>
                </div>
                <div className="cp-doc-item__actions">
                  {accepted[doc.id] && (
                    <span className="cp-doc-accepted-badge">✓ Accepted</span>
                  )}
                  {hasFile && (
                    <button className="cp-btn cp-btn--sm" onClick={() => handleView(doc)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      {viewed[doc.id] ? 'Re-view' : 'View'}
                    </button>
                  )}
                </div>
              </div>
              {needsAccept && !accepted[doc.id] && viewed[doc.id] && (
                <div className="cp-doc-accept-row">
                  <label className="cp-field__checkbox">
                    <input
                      type="checkbox"
                      checked={!!accepted[doc.id]}
                      onChange={e => e.target.checked && handleAccept(doc.id)}
                    />
                    <span>I have read and accept this document</span>
                  </label>
                </div>
              )}
              {needsAccept && !accepted[doc.id] && !viewed[doc.id] && (
                <div className="cp-doc-hint">Open the document and accept it to proceed.</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="cp-section__footer">
        <button className="cp-btn" onClick={onBack}>Back</button>
        <button className="cp-btn cp-btn--primary" onClick={onNext} disabled={!canContinue}>
          I've Reviewed All Documents
        </button>
      </div>

      {viewingDoc && (
        <DocViewerModal
          doc={viewingDoc}
          isAccepted={!!accepted[viewingDoc.id]}
          onAccept={handleAccept}
          onClose={() => setViewingDoc(null)}
        />
      )}
    </div>
  );
};

// ─── Done Step ────────────────────────────────────────────────────────────────
const DoneSection = ({ run, onBack }) => {
  const isCompleted = run?.status === 'completed';
  const [countdown, setCountdown] = useState(15);

  useEffect(() => {
    if (isCompleted) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!window.close()) window.location.href = 'about:blank';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isCompleted]);

  if (isCompleted) {
    return (
      <div className="cp-section cp-done">
        <div className="cp-done__icon">🎉</div>
        <h2 className="cp-done__title">You're all set!</h2>
        <p className="cp-done__msg">Your onboarding is complete. You can close this window.</p>
        <div className="cp-done__card">
          <div className="cp-done__card-title">What happens next?</div>
          <ul className="cp-done__list">
            <li>You'll receive a welcome email with further details</li>
            <li>On your first day, please bring your ID documents</li>
          </ul>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="cp-btn" onClick={onBack} style={{ flex: 1 }}>
              Back
            </button>
            <button
              className="cp-btn cp-btn--primary"
              onClick={() => { if (!window.close()) window.location.href = 'about:blank'; }}
              style={{ flex: 1 }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-section cp-done">
      <div className="cp-done__icon">📋</div>
      <h2 className="cp-done__title">Submitted for Review</h2>
      <p className="cp-done__msg">
        Thank you for completing your submission, {run?.candidate_name || 'there'}! Our HR team is now reviewing your information. You will be notified if any changes are required.
      </p>
      <div className="cp-done__card">
        <div className="cp-done__card-title">What happens next?</div>
        <ul className="cp-done__list">
          <li>HR will review your submitted forms and documents</li>
          <li>If changes are needed, you'll receive an email with instructions</li>
          <li>Once approved, your onboarding will be marked complete</li>
        </ul>
        <button
          className="cp-btn cp-btn--primary"
          onClick={() => { if (!window.close()) window.location.href = 'about:blank'; }}
          style={{ marginTop: 16, width: '100%' }}
        >
          Close Tab ({countdown}s)
        </button>
      </div>
    </div>
  );
};

// ─── Changes Requested View ───────────────────────────────────────────────────
const ChangesRequestedView = ({ run, onEdit }) => (
  <div className="cp-section cp-changes-requested">
    <div className="cp-changes-requested__icon">⚠️</div>
    <h2 className="cp-changes-requested__title">Changes Requested by HR</h2>
    <div className="cp-changes-requested__comment-box">
      <div className="cp-changes-requested__comment-label">HR Comment</div>
      <p className="cp-changes-requested__comment-text">
        {run?.meta_data?.review_comment || 'Please review and resubmit your onboarding information.'}
      </p>
    </div>
    <p className="cp-changes-requested__hint">
      Please review the feedback above, make the necessary corrections, and resubmit your onboarding.
    </p>
    <button className="cp-btn cp-btn--primary" onClick={onEdit} style={{ marginTop: 8 }}>
      Edit &amp; Resubmit
    </button>
  </div>
);

// ─── Candidate Portal ─────────────────────────────────────────────────────────
const CandidatePortal = () => {
  const [searchParams] = useSearchParams();
  const invite_token = searchParams.get('token');
  const dispatch = useDispatch();

  const tenantBranding = useSelector((state) => state.qualifyQuestions?.tenantBranding);
  const isWhiteLabel = Boolean(tenantBranding?.is_white_label);

  useEffect(() => {
    const brandingToken = searchParams.get('t');
    if (!brandingToken) return;
    dispatch(fetchTenantBranding(brandingToken));
  }, [searchParams, dispatch]);

  useEffect(() => {
    if (isWhiteLabel && tenantBranding?.colour) {
      document.documentElement.style.setProperty('--qualify-accent-color', tenantBranding.colour);
    }
  }, [isWhiteLabel, tenantBranding?.colour]);

  const [phase, setPhase] = useState('otp_request');
  const [sessionToken, setSessionToken] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpError, setOtpError] = useState('');
  const [portal, setPortal] = useState(null);
  const [step, setStep] = useState(0);
  const [formAnswers, setFormAnswers] = useState({});
  const [formsSubmitted, setFormsSubmitted] = useState({});
  const [docAccepted, setDocAccepted] = useState({});
  const [docViewed, setDocViewed] = useState({});
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [otpResendMsg, setOtpResendMsg] = useState('');
  const [isEditingAfterChanges, setIsEditingAfterChanges] = useState(false);
  const [allowBackFromCompleted, setAllowBackFromCompleted] = useState(false);
  const [snackbar, setSnackbar] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendTimerRef = useRef(null);

  const startResendTimer = useCallback(() => {
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    setResendCooldown(60);
    resendTimerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current);
          resendTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => {
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
  }, []);

  const sendOtp = useCallback(async () => {
    setOtpError('');
    const res = await dispatch(requestPortalOtp(invite_token));
    if (res?.success) {
      setMaskedEmail(res.data?.masked_email || res.data?.email || '');
      setPhase('otp_verify');
      startResendTimer();
      return true;
    } else if (res?.status === 429) {
      setSnackbar(res.message || 'Too many requests. Please wait before trying again.');
      setPhase(prev => prev === 'otp_request' ? 'otp_verify' : prev);
      startResendTimer();
      return false;
    } else {
      setErrorMsg(res?.message || 'Failed to send OTP. The link may be invalid or expired.');
      setPhase('error');
      return false;
    }
  }, [dispatch, invite_token, startResendTimer]);

  const handleResend = useCallback(async () => {
    setOtpResendMsg('');
    const success = await sendOtp();
    if (success) setOtpResendMsg('A new OTP has been sent to your email.');
  }, [sendOtp]);

  useEffect(() => { sendOtp(); }, [sendOtp]);

  const handleVerifyOtp = async () => {
    setSaving(true);
    setOtpError('');
    const res = await dispatch(verifyPortalOtp(invite_token, otpInput));
    if (res?.success) {
      const token = res.data?.access_token;
      setSessionToken(token);
      setPhase('loading');
      const portalRes = await dispatch(loadCandidatePortal(token));
      if (portalRes?.success) {
        const data = portalRes.data;
        setPortal(data);
        setFormsSubmitted(data?.run?.progress?.formsSubmitted || {});
        const apiResponses = data?.responses || {};
        const initialAnswers = {};
        (data?.forms || []).forEach(form => {
          const formKey = form.id ?? form.name;
          const formResp = apiResponses[form.id] || {};
          initialAnswers[formKey] = {};
          (form.fields || []).forEach((fld, i) => {
            const fieldKey = fld.id ?? `${fld.label ?? ''}_${i}`;
            const label = fld.label || `Field ${i + 1}`;
            if (formResp[label] !== undefined) {
              initialAnswers[formKey][fieldKey] = formResp[label];
            }
          });
        });
        setFormAnswers(initialAnswers);
        setStep(data?.run?.progress?.current_step || 0);
        setPhase('loaded');
      } else {
        setErrorMsg(portalRes?.message || 'Failed to load your onboarding.');
        setPhase('error');
      }
    } else if (res?.status === 429) {
      setSnackbar(res.message || 'Too many requests. Please wait before trying again.');
    } else {
      setOtpError(res?.message || 'Invalid OTP. Please try again.');
    }
    setSaving(false);
  };

  const activeSteps = (() => {
    if (!portal) return ['Welcome', 'Done'];
    const { run, forms, documents } = portal;
    const steps = ['Welcome'];
    if (run?.step_flags?.forms && (forms || []).length > 0) steps.push('Forms');
    if (run?.step_flags?.documents && (documents || []).length > 0) steps.push('Documents');
    steps.push('Done');
    return steps;
  })();

  const handlePortalFormFileUpload = async (formId, fieldIdx, formData) => {
    const res = await dispatch(uploadPortalFormFile(sessionToken, formId, fieldIdx, formData));
    if (!res?.success) return null;
    return res.data?.data ?? res.data;
  };

  // Submit a single form with label-keyed payload
  const handleFormSave = async (form, formKey) => {
    setSaving(true);
    const answers = formAnswers[formKey] || {};
    const responses = {};
    (form.fields || []).forEach((fld, i) => {
      const label = fld.label || `Field ${i + 1}`;
      const fieldKey = fld.id ?? `${fld.label ?? ''}_${i}`;
      responses[label] = answers[fieldKey] ?? '';
    });
    const res = await dispatch(submitPortalFormAnswers(sessionToken, form.id, { responses, mark_complete: true }));
    if (res?.success || res?.data) {
      setFormsSubmitted(prev => ({ ...prev, [form.id ?? formKey]: true }));
    }
    setSaving(false);
  };

  const handleNext = async () => {
    const nextStep = step + 1;
    setSaving(true);

    const advancingToDone = nextStep === activeSteps.length - 1;
    if (advancingToDone) {
      // Build docs progress from lifted DocumentsSection state
      const docsProgress = {};
      (portal?.documents || []).forEach(doc => {
        const docId = String(doc.id);
        const isViewed = !!(docViewed[docId] || doc.viewed);
        const isAccepted = !!(docAccepted[docId] || doc.accepted);
        if (doc.requires_accept) {
          docsProgress[docId] = { viewed: isViewed, accepted: isAccepted };
        } else {
          docsProgress[docId] = { viewed: isViewed, read: isViewed };
        }
      });
      await dispatch(finalSubmitPortal(sessionToken, {
        progress: {
          idx: nextStep,
          seq: activeSteps.map(s => s.toLowerCase()),
          docs: docsProgress,
          formIdx: 0,
          current_step: step,
          activeFormIdx: -1,
          formResponses: {},
          formsSubmitted: formsSubmitted,
        },
      }));
    } else {
      await dispatch(patchPortalProgress(sessionToken, { progress: { current_step: nextStep } }));
    }

    setStep(nextStep);
    setSaving(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStep(s => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (phase === 'otp_request') {
    return (
      <div className="cp-loading">
        <div className="cp-loading__spinner" />
        <p>Sending verification code…</p>
      </div>
    );
  }

  if (phase === 'otp_verify') {
    return (
      <div className="cp-root cp-root--otp">
        <Snackbar message={snackbar} onClose={() => setSnackbar('')} />
        <OtpGate
          maskedEmail={maskedEmail}
          otpInput={otpInput}
          setOtpInput={setOtpInput}
          onSubmit={handleVerifyOtp}
          onResend={handleResend}
          error={otpError}
          resendMsg={otpResendMsg}
          saving={saving}
          resendCooldown={resendCooldown}
        />
      </div>
    );
  }

  if (phase === 'loading') {
    return (
      <div className="cp-loading">
        <div className="cp-loading__spinner" />
        <p>Loading your onboarding portal…</p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="cp-error">
        <span className="cp-error__icon">⚠️</span>
        <h2>Link Invalid or Expired</h2>
        <p>{errorMsg}</p>
      </div>
    );
  }

  const { run, forms, documents, welcome: welcomeContent = {} } = portal;
  const runStatus = run?.status;
  const effectiveStep = (runStatus === 'completed' && !allowBackFromCompleted) ? activeSteps.length - 1 : step;
  const currentStepName = activeSteps[effectiveStep];

  return (
    <div className="cp-root">
      <StepPills steps={activeSteps} currentStep={effectiveStep} />

      {runStatus === 'in_review' && (
        <div className="cp-status-banner cp-status-banner--review">
          ⏳ Your submission is under HR review. You cannot edit until HR requests changes.
        </div>
      )}

      <div className="cp-body">
        <div className="cp-sidebar">
          <div className="cp-sidebar__candidate">
            <div className="cp-sidebar__avatar">
              {(run?.candidate_name || 'C').charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="cp-sidebar__name">{run?.candidate_name || 'Candidate'}</div>
              <div className="cp-sidebar__email">{run?.email}</div>
            </div>
          </div>
          <PortalProgress current={effectiveStep} total={activeSteps.length - 1} forceComplete={runStatus === 'completed'} />
          <nav className="cp-sidebar__nav">
            {activeSteps.map((s, i) => (
              <div
                key={s}
                className={`cp-nav-item ${i === effectiveStep ? 'active' : ''} ${i < effectiveStep ? 'done' : ''}`}
              >
                <div className="cp-nav-item__circle">
                  {i < effectiveStep ? (
                    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7l3.5 3.5L12 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <span>{i + 1}</span>
                  )}
                </div>
                <span>{s}</span>
              </div>
            ))}
          </nav>
        </div>

        <main className="cp-main">
          {runStatus === 'changes_requested' && !isEditingAfterChanges ? (
            <ChangesRequestedView
              run={run}
              onEdit={() => { setIsEditingAfterChanges(true); setStep(0); }}
            />
          ) : (
            <>
              {currentStepName === 'Welcome' && (
                <WelcomeSection run={run} welcome={welcomeContent} forms={forms || []} documents={documents || []} onNext={handleNext} />
              )}
              {currentStepName === 'Forms' && (
                <FormsSection
                  forms={forms || []}
                  formAnswers={formAnswers}
                  setFormAnswers={setFormAnswers}
                  formsSubmitted={formsSubmitted}
                  onFormSave={handleFormSave}
                  onNext={handleNext}
                  onBack={handleBack}
                  saving={saving}
                  onFileUpload={handlePortalFormFileUpload}
                />
              )}
              {currentStepName === 'Documents' && (
                <DocumentsSection
                  documents={documents || []}
                  onNext={handleNext}
                  onBack={handleBack}
                  accepted={docAccepted}
                  setAccepted={setDocAccepted}
                  viewed={docViewed}
                  setViewed={setDocViewed}
                />
              )}
              {currentStepName === 'Done' && (
                <DoneSection
                  run={run}
                  onBack={() => {
                    setAllowBackFromCompleted(true);
                    setStep(activeSteps.length - 2);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default CandidatePortal;
